import  { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Chip, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button,

  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon, 
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ApprovedIcon,
  PendingOutlined as PendingIcon,
  ErrorOutline as ReviewIcon
} from '@mui/icons-material';
import Layout from "../components/layout/CompliceLayout";

// Sample Policies Data
const initialPolicies = [
  {
    id: 1,
    title: 'Data Privacy Policy',
    category: 'Compliance',
    status: 'Approved',
    version: '2.0',
    lastUpdated: '2024-05-15',
    owner: 'Legal Department',
    summary: 'Comprehensive guidelines for handling personal and sensitive data',
    fullDocument: `
    1. Data Collection
    - Only collect necessary personal information
    - Obtain explicit consent from individuals
    - Clearly communicate data usage purposes

    2. Data Protection
    - Implement robust security measures
    - Encrypt sensitive data
    - Restrict access to authorized personnel only

    3. Compliance Requirements
    - Adhere to GDPR, CCPA, and other relevant regulations
    - Conduct regular privacy impact assessments
    - Maintain detailed records of data processing activities
    `,
    applicableDepartments: ['IT', 'HR', 'Marketing', 'Sales']
  },
  {
    id: 2,
    title: 'Information Security Policy',
    category: 'IT Security',
    status: 'Pending Review',
    version: '1.5',
    lastUpdated: '2024-04-20',
    owner: 'IT Security Team',
    summary: 'Guidelines for maintaining information security and preventing cyber threats',
    fullDocument: `
    1. Access Control
    - Implement multi-factor authentication
    - Regular password rotation
    - Role-based access management

    2. Network Security
    - Maintain updated firewall configurations
    - Use VPN for remote access
    - Regular security vulnerability scans

    3. Incident Response
    - Develop and maintain incident response plan
    - Conduct regular security awareness training
    - Establish clear reporting mechanisms for security incidents
    `,
    applicableDepartments: ['IT', 'Finance', 'Operations']
  },
  {
    id: 3,
    title: 'Remote Work Policy',
    category: 'HR',
    status: 'Under Review',
    version: '1.2',
    lastUpdated: '2024-03-10',
    owner: 'HR Department',
    summary: 'Guidelines and expectations for remote and hybrid work arrangements',
    fullDocument: `
    1. Eligibility
    - Define roles suitable for remote work
    - Performance-based remote work eligibility
    - Clear communication expectations

    2. Work Arrangements
    - Flexible working hours
    - Performance measurement criteria
    - Required availability during core business hours

    3. Equipment and Security
    - Company-provided equipment guidelines
    - Data protection requirements
    - Secure home office setup recommendations
    `,
    applicableDepartments: ['All Departments']
  }
];

// Policy Status Chip Component
const PolicyStatusChip = ({ status }) => {
  const getChipProps = () => {
    switch (status) {
      case 'Approved':
        return { color: 'success', icon: <ApprovedIcon /> };
      case 'Pending Review':
        return { color: 'warning', icon: <PendingIcon /> };
      case 'Under Review':
        return { color: 'info', icon: <ReviewIcon /> };
      default:
        return { color: 'default' };
    }
  };

  const chipProps = getChipProps();

  return (
    <Chip 
      label={status} 
      {...chipProps} 
      variant="outlined"
      size="small"
    />
  );
};


const ComplicePolicy = () => {
    const [policies,] = useState(initialPolicies);
    const [selectedPolicy, setSelectedPolicy] = useState(null);
    const [filters, setFilters] = useState({
      category: '',
      status: ''
    });
    const [tabValue, setTabValue] = useState(0);
  
    // Policy Details Modal
    const handleOpenDetails = (policy) => {
      setSelectedPolicy(policy);
    };
  
    const handleCloseDetails = () => {
      setSelectedPolicy(null);
    };
  
    // Filtering
    const filteredPolicies = policies.filter(policy => {
      const matchesCategory = filters.category 
        ? policy.category === filters.category 
        : true;
      const matchesStatus = filters.status 
        ? policy.status === filters.status 
        : true;
  
      return matchesCategory && matchesStatus;
    });
  return (
    <Layout>
        <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Policies Management
      </Typography>

      {/* Filtering Section */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Policy Category</InputLabel>
            <Select
              value={filters.category}
              onChange={(e) => setFilters(prev => ({...prev, category: e.target.value}))}
              label="Policy Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              <MenuItem value="Compliance">Compliance</MenuItem>
              <MenuItem value="IT Security">IT Security</MenuItem>
              <MenuItem value="HR">HR</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth variant="outlined">
            <InputLabel>Policy Status</InputLabel>
            <Select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
              label="Policy Status"
            >
              <MenuItem value="">All Statuses</MenuItem>
              <MenuItem value="Approved">Approved</MenuItem>
              <MenuItem value="Pending Review">Pending Review</MenuItem>
              <MenuItem value="Under Review">Under Review</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Policies Table */}
      <Paper elevation={3}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Policy Title</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPolicies.map((policy) => (
                <TableRow key={policy.id}>
                  <TableCell>{policy.title}</TableCell>
                  <TableCell>{policy.category}</TableCell>
                  <TableCell>{policy.version}</TableCell>
                  <TableCell>{policy.lastUpdated}</TableCell>
                  <TableCell>
                    <PolicyStatusChip status={policy.status} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenDetails(policy)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Policy">
                      <IconButton size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Policy">
                      <IconButton size="small">
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Policy Details Dialog */}
      <Dialog 
        open={!!selectedPolicy} 
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        {selectedPolicy && (
          <>
            <DialogTitle>
              {selectedPolicy.title} - Policy Details
              <Typography variant="subtitle2" color="text.secondary">
                Version {selectedPolicy.version}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Tabs 
                value={tabValue} 
                onChange={(e, newValue) => setTabValue(newValue)}
                sx={{ mb: 2 }}
              >
                <Tab label="Overview" />
                <Tab label="Full Document" />
                <Tab label="Departments" />
              </Tabs>

              {tabValue === 0 && (
                <Box>
                  <Typography variant="subtitle1">
                    <strong>Category:</strong> {selectedPolicy.category}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Owner:</strong> {selectedPolicy.owner}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Last Updated:</strong> {selectedPolicy.lastUpdated}
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>Status:</strong> <PolicyStatusChip status={selectedPolicy.status} />
                  </Typography>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    <strong>Summary:</strong> {selectedPolicy.summary}
                  </Typography>
                </Box>
              )}

              {tabValue === 1 && (
                <Accordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                  >
                    <Typography>Full Policy Document</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography 
                      variant="body2" 
                      component="pre" 
                      sx={{ 
                        whiteSpace: 'pre-wrap', 
                        fontFamily: 'monospace',
                        backgroundColor: '#f4f4f4',
                        p: 2,
                        borderRadius: 1
                      }}
                    >
                      {selectedPolicy.fullDocument}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              )}

              {tabValue === 2 && (
                <Box>
                  <Typography variant="subtitle1">
                    <strong>Applicable Departments:</strong>
                  </Typography>
                  {selectedPolicy.applicableDepartments.map((dept) => (
                    <Chip 
                      key={dept} 
                      label={dept} 
                      variant="outlined" 
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDetails} color="primary">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>

    </Layout>
  )
}

export default ComplicePolicy